import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, SlidersHorizontal, Users, Crosshair } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { countryInfo, GENDERS } from "../constants/options";
import { CountryPicker } from "../components/CountryPicker";
import { ExtraCard } from "../components/ExtraCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ANY = "__any";
const FIELDS = ["country", "near", "radius", "location", "region", "gender", "age", "skill", "q"];
const RADII = ["5", "10", "25", "50", "100", "250"];
const NEAR_HINT = {
  "United Kingdom": "e.g. M1 1AE, SW1A or Leeds",
  "United States": "e.g. 90028, Atlanta or Brooklyn",
  Ireland: "e.g. Dublin 2, Galway or Bray",
};

const NearRow = ({ form, set, setCountry }) => (
  <div className="md:col-span-6 grid grid-cols-1 md:grid-cols-6 border-b border-white/10" data-testid="search-near-row">
    <div className="md:col-span-2 p-4 border-b md:border-b-0 md:border-r border-white/10">
      <label className="label-xs">Country</label>
      <CountryPicker value={form.country} onChange={setCountry} className="mt-2" />
    </div>
    <div className="md:col-span-3 p-4 border-b md:border-b-0 md:border-r border-white/10">
      <label className="label-xs flex items-center gap-2"><Crosshair size={12} className="text-primary" />Near postcode, ZIP or town</label>
      <Input value={form.near} onChange={(e) => set("near")(e.target.value)} placeholder={NEAR_HINT[form.country] || "e.g. M1 1AE, 90028 or Dublin"} className="mt-2 bg-transparent border-0 px-0 text-base focus-visible:ring-0" data-testid="search-near-input" />
    </div>
    <div className="md:col-span-1 p-4">
      <label className="label-xs">Within</label>
      <Select value={form.radius || "25"} onValueChange={set("radius")}>
        <SelectTrigger className="mt-2 bg-transparent border-0 px-0 focus:ring-0" data-testid="search-radius-select"><SelectValue /></SelectTrigger>
        <SelectContent>{RADII.map((r) => <SelectItem key={r} value={r}>{r} miles</SelectItem>)}</SelectContent>
      </Select>
    </div>
  </div>
);

const Filters = ({ form, setForm, onSubmit }) => {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const setCountry = (c) => setForm({ ...form, country: c, region: "" });
  const info = form.country ? countryInfo(form.country) : null;
  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-6 border border-white/10 bg-card" data-testid="search-filters-form">
      <NearRow form={form} set={set} setCountry={setCountry} />
      <div className="md:col-span-2 p-4 border-b md:border-b-0 md:border-r border-white/10">
        <label className="label-xs">Location name</label>
        <Input value={form.location} onChange={(e) => set("location")(e.target.value)} placeholder="Town or postcode / ZIP" className="mt-2 bg-transparent border-0 px-0 text-base focus-visible:ring-0" data-testid="search-location-input" />
      </div>
      <div className="p-4 border-b md:border-b-0 md:border-r border-white/10">
        <label className="label-xs">{info ? info.regionLabel : "Region / State / County"}</label>
        <Select value={form.region || ANY} onValueChange={(v) => set("region")(v === ANY ? "" : v)} disabled={!info}>
          <SelectTrigger className="mt-2 bg-transparent border-0 px-0 focus:ring-0" data-testid="search-region-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{info ? `Any ${info.regionLabel.toLowerCase()}` : "Pick a country first"}</SelectItem>
            {(info?.regions || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="p-4 border-b md:border-b-0 md:border-r border-white/10">
        <label className="label-xs">Gender</label>
        <Select value={form.gender || ANY} onValueChange={(v) => set("gender")(v === ANY ? "" : v)}>
          <SelectTrigger className="mt-2 bg-transparent border-0 px-0 focus:ring-0" data-testid="search-gender-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any</SelectItem>
            {GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="p-4 border-b md:border-b-0 md:border-r border-white/10">
        <label className="label-xs">Age</label>
        <Input type="number" min="0" max="110" value={form.age} onChange={(e) => set("age")(e.target.value)} placeholder="e.g. 35" className="mt-2 bg-transparent border-0 px-0 text-base focus-visible:ring-0" data-testid="search-age-input" />
      </div>
      <button className="bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 py-4 hover:bg-[hsl(43,100%,60%)] transition-colors" data-testid="search-submit-button">
        <SearchIcon size={16} /> Search
      </button>
      <div className="md:col-span-6 p-4 border-t border-white/10 flex flex-col md:flex-row gap-4">
        <div className="flex items-center gap-3 flex-1">
          <SlidersHorizontal size={16} className="text-white/40" />
          <Input value={form.skill} onChange={(e) => set("skill")(e.target.value)} placeholder="Skill (e.g. driving, dancing)" className="bg-transparent border-0 px-0 focus-visible:ring-0" data-testid="search-skill-input" />
        </div>
        <Input value={form.q} onChange={(e) => set("q")(e.target.value)} placeholder="Keyword (name or description)" className="bg-transparent border-0 px-0 flex-1 focus-visible:ring-0" data-testid="search-keyword-input" />
      </div>
    </form>
  );
};

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initial = Object.fromEntries(FIELDS.map((f) => [f, params.get(f) || ""]));
  const [form, setForm] = useState(initial);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const query = Object.fromEntries(FIELDS.map((f) => [f, params.get(f) || ""]).filter(([, v]) => v));
    if (!query.near) delete query.radius;
    setResults(null);
    setError("");
    api.get("/extras", { params: query }).then((r) => setResults(r.data)).catch((e) => {
      setError(formatApiError(e));
      setResults([]);
    });
  }, [params]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams(Object.fromEntries(Object.entries(form).filter(([, v]) => v)));
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="search-page">
      <p className="label-xs">Directory</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3">Find extras</h1>
      <p className="text-white/60 mt-4 max-w-xl">Extras across the UK, USA and Ireland. Pick a country and search near a postcode, ZIP or town to find background artists within a set distance of your shoot.</p>
      <div className="mt-10">
        <Filters form={form} setForm={setForm} onSubmit={onSubmit} />
      </div>
      <div className="mt-10 flex items-center justify-between">
        <p className="label-xs" data-testid="search-results-count">
          {results === null ? "Searching…" : `${results.length} extra${results.length === 1 ? "" : "s"} found`}
        </p>
      </div>
      {error && <p className="mt-6 border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-300" data-testid="search-error">{error}</p>}
      {results && results.length === 0 && !error && (
        <div className="mt-6 border border-dashed border-white/15 p-16 text-center" data-testid="search-empty-state">
          <Users className="mx-auto text-white/20" size={48} />
          <p className="font-display text-2xl mt-6">No extras match yet</p>
          <p className="text-white/50 mt-2 text-sm">Try a wider area, a nearby town, or remove some filters.</p>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-6" data-testid="search-results-grid">
        {(results || []).map((e, i) => <ExtraCard key={e.slug} extra={e} index={i} />)}
      </div>
    </div>
  );
}
